import BrowserUtil from '@/platform/oss/browserUtil'
import { PROJECT_CURRENT_DIR } from '@/features/project-cloud/ossPaths'
import type { ProjectArchiveConfig, ProjectArchiveItem } from './archiveTypes'

const ARCHIVE_DIR = 'backup'
const ARCHIVE_CONFIG_FILE = `${ARCHIVE_DIR}/config.json`
const MAX_ARCHIVE_COUNT = 10
const AUTO_ARCHIVE_DESCRIPTION = '自动保存归档'
const autoArchiveQueues = new Map<string, Promise<unknown>>()

type CopyProgress = {
  current: number
  total: number
  fileName: string
}

function pad(value: number, size = 2): string {
  return String(value).padStart(size, '0')
}

function formatDisplayTime(date = new Date()): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

function formatArchiveId(date = new Date()): string {
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
    pad(date.getHours()),
    pad(date.getMinutes()),
    pad(date.getSeconds()),
    pad(date.getMilliseconds(), 3),
  ].join('')
}

function getArchiveCreatedAtTime(item: ProjectArchiveItem): number {
  const time = Date.parse(item.createdAt.replace(' ', 'T'))
  return Number.isFinite(time) ? time : 0
}

function normalizeConfig(value: unknown): ProjectArchiveConfig {
  const record = value && typeof value === 'object' ? value as Partial<ProjectArchiveConfig> : {}
  const items = Array.isArray(record.items)
    ? record.items
      .map((item) => {
        const row = item && typeof item === 'object' ? item as Partial<ProjectArchiveItem> : {}
        const folderName = String(row.folderName ?? row.id ?? '').trim()
        if (!folderName) return null
        return {
          id: String(row.id ?? folderName).trim() || folderName,
          folderName,
          createdAt: String(row.createdAt ?? '').trim(),
          description: String(row.description ?? '').trim(),
        } satisfies ProjectArchiveItem
      })
      .filter((item): item is ProjectArchiveItem => item !== null)
    : []
  return { version: 1, items }
}

function sortArchiveItemsByTimeDesc(items: ProjectArchiveItem[]): ProjectArchiveItem[] {
  return [...items]
    .sort((left, right) => {
      const timeDelta = getArchiveCreatedAtTime(right) - getArchiveCreatedAtTime(left)
      return timeDelta || right.folderName.localeCompare(left.folderName)
    })
}

async function readArchiveConfig(oss: BrowserUtil, guid: string): Promise<ProjectArchiveConfig> {
  try {
    const text = await oss.getText(`${guid}/${ARCHIVE_CONFIG_FILE}`)
    return normalizeConfig(JSON.parse(text) as unknown)
  } catch {
    return { version: 1, items: [] }
  }
}

async function writeArchiveConfig(oss: BrowserUtil, guid: string, config: ProjectArchiveConfig): Promise<void> {
  await oss.writeData(`${guid}/${ARCHIVE_CONFIG_FILE}`, JSON.stringify(config, null, 2), {
    contentType: 'application/json;charset=utf-8',
  })
}

async function copyFolder(params: {
  oss: BrowserUtil
  from: string
  to: string
  onProgress?: (progress: CopyProgress) => void
}): Promise<void> {
  const objects = (await params.oss.listObjects(params.from)).filter(
    (item) => item.relativePath && !item.key.endsWith('/'),
  )
  if (!objects.length) {
    throw new Error(`没有可复制的文件：${params.from}`)
  }
  await params.oss.createFolder(params.to)
  for (let index = 0; index < objects.length; index += 1) {
    const object = objects[index]!
    const source = `${params.from}/${object.relativePath}`
    const target = `${params.to}/${object.relativePath}`
    params.onProgress?.({ current: index + 1, total: objects.length, fileName: object.relativePath })
    await params.oss.copyObject(source, target)
  }
}

async function createAutoProjectArchiveNow(params: {
  guid: string
  uid: string | number
  onProgress?: (message: string) => void
}): Promise<ProjectArchiveItem> {
  const oss = BrowserUtil.getInstance().connect(params.uid)
  const now = new Date()
  const config = await readArchiveConfig(oss, params.guid)
  const archiveId = formatArchiveId(now)
  const item: ProjectArchiveItem = {
    id: archiveId,
    folderName: archiveId,
    createdAt: formatDisplayTime(now),
    description: AUTO_ARCHIVE_DESCRIPTION,
  }
  params.onProgress?.('复制 current 到自动归档目录…')
  await copyFolder({
    oss,
    from: `${params.guid}/${PROJECT_CURRENT_DIR}`,
    to: `${params.guid}/${ARCHIVE_DIR}/${archiveId}`,
    onProgress: (progress) => {
      params.onProgress?.(`复制自动归档文件 ${progress.current}/${progress.total} · ${progress.fileName}`)
    },
  })
  const nextItems = sortArchiveItemsByTimeDesc([
    item,
    ...config.items.filter((entry) => entry.folderName !== archiveId),
  ])
  const removed = nextItems.splice(MAX_ARCHIVE_COUNT)
  for (const oldItem of removed) {
    await oss.deleteFolder(`${params.guid}/${ARCHIVE_DIR}/${oldItem.folderName}`)
  }
  await writeArchiveConfig(oss, params.guid, { version: 1, items: nextItems })
  return item
}

/**
 * 在保存成功后创建按时间戳命名的自动归档，超过 10 个后删除最旧归档。
 * @param params 工程 guid、登录 uid 和可选进度回调
 * @returns 本次写入的自动归档记录。
 */
export async function createAutoProjectArchive(params: {
  guid: string
  uid: string | number
  onProgress?: (message: string) => void
}): Promise<ProjectArchiveItem> {
  const queueKey = `${String(params.uid)}:${params.guid}`
  const previous = autoArchiveQueues.get(queueKey) ?? Promise.resolve()
  const current = previous
    .catch(() => undefined)
    .then(() => createAutoProjectArchiveNow(params))
  autoArchiveQueues.set(queueKey, current)
  try {
    return await current
  } finally {
    if (autoArchiveQueues.get(queueKey) === current) {
      autoArchiveQueues.delete(queueKey)
    }
  }
}
